/**
 * 知识库合并：将用户报表知识库与内置范例知识库合并。
 *
 * 合并策略：用户报表数据更准确，优先级更高（权重 +30%）。
 * 对于相同的 key，采用加权平均置信度；对于用户独有的 key，直接采用；
 * 对于内置独有的 key，降权后保留（备选方案）。
 */

import type { BindingDict, BindingDictEntry } from './bindingDict.js';

interface MergeOptions {
  userWeight?: number; // 用户报表权重倍数（缺省 1.3 = +30%）
  builtinWeight?: number; // 内置范例权重倍数（缺省 1.0）
}

/**
 * 合并两个知识库条目
 */
function mergeEntry(
  userEntry: BindingDictEntry | undefined,
  builtinEntry: BindingDictEntry | undefined,
  userWeight: number = 1.3,
  builtinWeight: number = 1.0,
): BindingDictEntry {
  if (!userEntry && !builtinEntry) {
    return { fields: [], confidence: [], count: 0 };
  }

  if (!userEntry) {
    return { ...builtinEntry! };
  }

  if (!builtinEntry) {
    return { ...userEntry };
  }

  // 合并字段列表（用户优先，去重）
  const mergedFields: string[] = [];
  const fieldConfMap = new Map<string, number>();

  // 先加入用户字段
  for (let i = 0; i < userEntry.fields.length; i++) {
    const field = userEntry.fields[i]!;
    const userConf = userEntry.confidence[i] ?? 0.5;
    mergedFields.push(field);
    fieldConfMap.set(field, userConf * userWeight);
  }

  // 再加入内置字段（如果不重复）
  for (let i = 0; i < builtinEntry.fields.length; i++) {
    const field = builtinEntry.fields[i]!;
    if (!mergedFields.includes(field)) {
      mergedFields.push(field);
      const builtinConf = builtinEntry.confidence[i] ?? 0.5;
      fieldConfMap.set(field, builtinConf * builtinWeight);
    } else {
      // 相同字段：加权平均
      const userConf = userEntry.confidence[userEntry.fields.indexOf(field)] ?? 0.5;
      const builtinConf = builtinEntry.confidence[i] ?? 0.5;
      const merged = (userConf * userWeight + builtinConf * builtinWeight) / (userWeight + builtinWeight);
      fieldConfMap.set(field, merged);
    }
  }

  // 归一化置信度到 [0, 1]
  const maxConf = Math.max(...Array.from(fieldConfMap.values()));
  const confidence = mergedFields.map((field) => {
    const rawConf = fieldConfMap.get(field) || 0;
    return Math.min(rawConf / (maxConf || 1), 1.0);
  });

  // 合并 sources
  const sources = new Set<string>();
  if (userEntry.sources) {
    for (const s of userEntry.sources) {
      if (typeof s === 'string') sources.add(s);
    }
  }
  if (builtinEntry.sources) {
    for (const s of builtinEntry.sources) {
      if (typeof s === 'string') sources.add(s);
    }
  }

  return {
    fields: mergedFields,
    confidence,
    count: (userEntry.count || 0) + (builtinEntry.count || 0),
    sources: sources.size > 0 ? Array.from(sources) : undefined,
  };
}

/**
 * 合并两个知识库（用户优先）
 *
 * @param userDict 用户报表知识库（高优先级）
 * @param builtinDict 内置范例知识库（低优先级）
 * @param options 合并选项
 * @returns 合并后的知识库
 */
export function mergeBindingDicts(
  userDict: BindingDict,
  builtinDict: BindingDict,
  options: MergeOptions = {},
): BindingDict {
  const { userWeight = 1.3, builtinWeight = 1.0 } = options;

  // 合并 labels
  const mergedLabels: Record<string, BindingDictEntry> = {};
  const allLabelKeys = new Set<string>([
    ...Object.keys(userDict.labels),
    ...Object.keys(builtinDict.labels),
  ]);

  for (const key of allLabelKeys) {
    const userEntry = userDict.labels[key];
    const builtinEntry = builtinDict.labels[key];
    mergedLabels[key] = mergeEntry(userEntry, builtinEntry, userWeight, builtinWeight);
  }

  // 合并 positions
  const mergedPositions: Record<string, BindingDictEntry> = {};
  const allPosKeys = new Set<string>([
    ...Object.keys(userDict.positions),
    ...Object.keys(builtinDict.positions),
  ]);

  for (const key of allPosKeys) {
    const userEntry = userDict.positions[key];
    const builtinEntry = builtinDict.positions[key];
    mergedPositions[key] = mergeEntry(userEntry, builtinEntry, userWeight, builtinWeight);
  }

  // 合并 patterns
  const mergedPatterns: Record<string, BindingDictEntry> = {};
  const allPatternKeys = new Set<string>([
    ...Object.keys(userDict.patterns),
    ...Object.keys(builtinDict.patterns),
  ]);

  for (const key of allPatternKeys) {
    const userEntry = userDict.patterns[key];
    const builtinEntry = builtinDict.patterns[key];
    mergedPatterns[key] = mergeEntry(userEntry, builtinEntry, userWeight, builtinWeight);
  }

  return {
    labels: mergedLabels,
    positions: mergedPositions,
    patterns: mergedPatterns,
    version: Math.max(userDict.version, builtinDict.version) + 1,
    miningDate: new Date().toISOString(),
    exampleCount: (userDict.exampleCount || 0) + (builtinDict.exampleCount || 0),
    bindingCount: (userDict.bindingCount || 0) + (builtinDict.bindingCount || 0),
  };
}

/**
 * 计算合并统计信息
 */
export function getMergeStats(
  userDict: BindingDict,
  builtinDict: BindingDict,
  mergedDict: BindingDict,
): {
  userLabelCount: number;
  builtinLabelCount: number;
  mergedLabelCount: number;
  userOnlyLabels: number;
  builtinOnlyLabels: number;
  commonLabels: number;
} {
  const userLabels = new Set(Object.keys(userDict.labels));
  const builtinLabels = new Set(Object.keys(builtinDict.labels));

  const commonLabels = new Set(
    [...userLabels].filter((l) => builtinLabels.has(l)),
  ).size;
  const userOnlyLabels = userLabels.size - commonLabels;
  const builtinOnlyLabels = builtinLabels.size - commonLabels;

  return {
    userLabelCount: userLabels.size,
    builtinLabelCount: builtinLabels.size,
    mergedLabelCount: Object.keys(mergedDict.labels).length,
    userOnlyLabels,
    builtinOnlyLabels,
    commonLabels,
  };
}
